import { MATERIAL_ICON_DATA } from './material-icon-data';

const selector =
  '.material-symbols-outlined, .material-symbols-filled, .material-icons';
const installed = new WeakMap<Document, () => void>();
const urls = new Map<string, string>();

function image(svg: string): string {
  let url = urls.get(svg);
  if (!url) {
    url = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
    urls.set(svg, url);
  }
  return url;
}

/** Font-independent compatibility for existing Angular-bound ligature hosts.
 * Text nodes remain untouched so Angular updates and accessible names keep working.
 */
export function installMaterialIcons(document: Document): () => void {
  const existing = installed.get(document);
  if (existing) return existing;
  const update = (element: Element) => {
    if (!(element instanceof HTMLElement)) return;
    if (!element.matches(selector)) {
      element.style.removeProperty('--pulse-icon');
      element.style.removeProperty('--pulse-icon-filled');
      return;
    }
    const name = element.textContent?.trim() ?? '';
    if (!name) {
      element.style.setProperty('--pulse-icon', 'none');
      element.style.setProperty('--pulse-icon-filled', 'none');
      return;
    }
    const entry = Object.prototype.hasOwnProperty.call(MATERIAL_ICON_DATA, name)
      ? MATERIAL_ICON_DATA[name]
      : undefined;
    if (entry) {
      element.style.setProperty('--pulse-icon', image(entry[0]));
      element.style.setProperty('--pulse-icon-filled', image(entry[1]));
    } else {
      element.style.removeProperty('--pulse-icon');
      element.style.removeProperty('--pulse-icon-filled');
    }
  };
  const scan = (node: Node) => {
    if (node instanceof Element) {
      if (node.matches(selector)) update(node);
      node.querySelectorAll(selector).forEach(update);
    }
  };
  scan(document.documentElement);
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes') update(record.target as Element);
      else if (record.type === 'characterData') {
        const host = record.target.parentElement?.closest(selector);
        if (host) update(host);
      } else {
        const host =
          record.target instanceof Element
            ? record.target.closest(selector)
            : null;
        if (host) update(host);
        record.addedNodes.forEach(scan);
      }
    }
  });
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['class'],
  });
  const dispose = () => {
    observer.disconnect();
    installed.delete(document);
  };
  installed.set(document, dispose);
  return dispose;
}
